import { axiosJSONInstance } from '@/config';
import {
  OrderBoxInput,
  ServicePickup,
  ServicePreferences,
  ServicePreferencesPayload,
  ServiceResolution,
} from '@/types';
import { multiBoxListOrNone } from '@/utils/orderBoxes';

// The merchant's service catalogue + their standing rules.
export const fetchServicePreferences = async (): Promise<ServicePreferences> => {
  const response = await axiosJSONInstance.get('/merchants/service-preferences');
  return response.data?.object;
};

// A REPLACE, not a patch — the whole rule set goes up in one call. That mirrors
// the merchant's mental model ("these are my rules") and is what makes removing
// a province override possible without a second endpoint.
//
// The endpoint echoes the freshly-persisted state, so the caller can seed the
// cache from the response instead of a follow-up GET.
export const updateServicePreferences = async (
  payload: ServicePreferencesPayload,
): Promise<ServicePreferences> => {
  const response = await axiosJSONInstance.put('/merchants/service-preferences', payload);
  return response.data?.object;
};

// SVC-STEP increment 2 — "what would an order to THIS destination ship on, and
// why?" Separate from the catalogue call on purpose: that one is
// destination-blind, and skipping the service step on its answer would
// override a merchant's province rule on every order without showing it.
export const resolveService = async (
  provinceCode?: string | null,
  temperatureControl?: boolean,
  regionUid?: string | null,
  pickup?: ServicePickup,
  boxes?: OrderBoxInput[],
): Promise<ServiceResolution> => {
  // Sent only when the order actually IS cold. Omitting it asks the plain
  // province question, which is what every caller did before 2026-08-26 and
  // what the backend still answers unchanged.
  const params: Record<string, unknown> = {};
  if (provinceCode) params.province_code = provinceCode;
  if (temperatureControl) params.temperature_control = '1';
  // BOXY-113 §8.3 — the destination REGION, so a service that cannot reach a
  // remote address comes back `available: false` with its reason.
  if (regionUid) params.region_uid = regionUid;
  // BOXY-530 — where the parcel is collected: a service no carrier collects
  // from there comes back disabled with `pickup_not_served`. Absent = the
  // merchant's default pickup address, as the submit gate does.
  if (pickup?.saved_pick_up_address_uid) {
    params.saved_pick_up_address_uid = pickup.saved_pick_up_address_uid;
  }
  if (pickup?.pick_up_address_uid) params.pick_up_address_uid = pickup.pick_up_address_uid;
  // BOXY-547 — the boxes, as the order payload sends them (2+ only): a service
  // the multi-box gate would refuse comes back disabled with `multi_box`.
  const multiBox = multiBoxListOrNone(boxes);
  if (multiBox) params.boxes = multiBox.map((box) => ({ size: box.size }));

  const response = await axiosJSONInstance.get('/merchants/service-preferences/resolve', {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return response.data?.object;
};
